import React, { useEffect, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'src/redux/hooks';
import { escapeRegExp } from 'lodash';

import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  TextField,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  Stack,
  styled,
  ListProps,
  Link,
  Typography,
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import { CountryFlag, CustomButton } from 'src/components';

import {
  fetchReferralJobOpenings,
  referralJobOpeningsSelectors,
} from 'src/redux/referralJobOpenings';

import { isStatusIdle } from 'src/utils';

const StyledList = styled(List)<ListProps>({
  width: '100%',
  '& .MuiListItem-root': {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '1rem',
    marginBottom: 0,
  },
  '& .MuiListItemText-root': {
    margin: 0,
  },
  '& .ae-custom-button_front': {
    paddingTop: '0.50rem !important',
    paddingBottom: '0.50rem !important',
  },
  '& .ae-custom-button_text': {
    fontSize: '0.813rem !important',
  },
});

export const ReferralJobOpeningModal: React.FC<{
  isOpen: boolean;
  handleClose: () => void;
  handleSelectJobOpening: (payload: string) => void;
}> = ({ isOpen, handleClose, handleSelectJobOpening }) => {
  const dispatch = useAppDispatch();
  const apiStatus = useAppSelector(
    referralJobOpeningsSelectors.getReferralJobOpeningsApiStatus,
  );

  const jobOpenings = useAppSelector(
    referralJobOpeningsSelectors.getReferralJobOpenings,
  );

  const [searchString, setSearchString] = useState<string>('');

  useEffect(() => {
    if (isStatusIdle(apiStatus)) {
      dispatch(fetchReferralJobOpenings());
    }
  }, [apiStatus]);

  useEffect(() => {
    if (!isOpen && searchString) {
      setSearchString('');
    }
  }, [isOpen, searchString]);

  const filteredJobOpenings = useMemo(() => {
    if (!searchString) return jobOpenings;

    const searchTerm = escapeRegExp(searchString.toLowerCase());
    return (jobOpenings ?? []).filter(
      (jobOpening) =>
        jobOpening.LP_Specializations?.toLowerCase().match(
          new RegExp(searchTerm, 'g'),
        ) ||
        jobOpening.TechnicalFlow?.toLowerCase().match(
          new RegExp(searchTerm, 'g'),
        ) ||
        jobOpening.Stack?.toLowerCase().match(new RegExp(searchTerm, 'g')) ||
        jobOpening.Client_Name?.toLowerCase().match(
          new RegExp(searchTerm, 'g'),
        ) ||
        jobOpening.Marketing_Title?.toLowerCase().match(
          new RegExp(searchTerm, 'g'),
        ) ||
        jobOpening.Job_Opening_Name?.toLowerCase().match(
          new RegExp(searchTerm, 'g'),
        ),
    );
  }, [jobOpenings, searchString]);

  return (
    <Dialog
      fullWidth
      open={isOpen}
      onClose={handleClose}
      PaperProps={{
        sx: {
          maxWidth: { xs: '100%', sm: '46.5rem' },
          height: { xs: '100%', sm: '34rem' },
          margin: { xs: 0, sm: '32px' },
          width: { xs: '100%' },
          maxHeight: { xs: '100%' },
        },
      }}
    >
      <DialogTitle variant="h3" color="primary" marginBottom="0">
        Select position
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={handleClose}
        sx={{
          position: 'absolute',
          right: 12,
          top: 12,
        }}
      >
        <CloseRoundedIcon color="secondary" fontSize="small" />
      </IconButton>
      <DialogContent>
        <Stack direction="column" gap="1rem" height="100%">
          <TextField
            fullWidth
            hiddenLabel
            placeholder="Search by specialization, stack, client, etc"
            type="search"
            variant="outlined"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRoundedIcon />
                </InputAdornment>
              ),
            }}
            value={searchString}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setSearchString(event.target.value);
            }}
          />
          <StyledList sx={{ width: '100%', overflowY: 'auto' }}>
            {filteredJobOpenings?.map((jobOpening) => (
              <ListItem key={jobOpening.id}>
                <Stack
                  sx={{
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: { xs: 'start', sm: 'center' },
                    justifyContent: 'space-between',
                    width: '100%',
                    gap: 1,
                    paddingBottom: { xs: '10px' },
                  }}
                >
                  <ListItemText
                    id={jobOpening.id}
                    primary={
                      <Stack
                        flexDirection="column"
                        alignItems="start"
                        flexWrap="wrap"
                      >
                        <Typography variant="body1">
                          {jobOpening.Marketing_Title}
                        </Typography>

                        <Stack flexDirection="row" flexWrap="wrap" gap={0}>
                          {JSON.parse(jobOpening?.Location_Area)?.map(
                            (country: string) => (
                              <CountryFlag country={country} key={country} />
                            ),
                          )}
                        </Stack>
                      </Stack>
                    }
                    secondary={jobOpening.Client_Name}
                  />
                  <Stack
                    direction="row"
                    gap="1rem"
                    sx={{
                      sm: 'column',
                      width: { xs: '100%', sm: '30%' },
                      justifyContent: { xs: 'space-between' },
                    }}
                  >
                    <Stack direction="row" gap="0.25rem">
                      <Link
                        target="_blank"
                        rel="noopener"
                        variant="body2"
                        whiteSpace="nowrap"
                        href={jobOpening.Careers_Site_Link}
                      >
                        More info
                      </Link>
                      <OpenInNewRoundedIcon sx={{ fontSize: '1.125rem' }} />
                    </Stack>

                    <CustomButton
                      fullWidth={true}
                      maxWidth={'6.625rem'}
                      onClick={() => handleSelectJobOpening(jobOpening.id)}
                      label={'Select'}
                    />
                  </Stack>
                </Stack>
              </ListItem>
            ))}
          </StyledList>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};
